import React, { forwardRef, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useTheme } from 'react-jss'
import { Doughnut } from 'react-chartjs-2'

import { insertIf } from '../../../../helpers/common'
import getReusableGraphOptions from '../reusableGraphOptions'

import {
  darkBlue,
  purple,
  instagramOrange,
  facebookBlue,
  trafficGreenDark,
  trafficYellowDark
} from '../../../../styles/const/colors'
import {
  DEFAULT_GRAPH_COLOR,
  GRAPH_COLOR_1,
  GRAPH_COLOR_2,
  GRAPH_COLOR_3
} from '../../../../styles/themes/getDefaultTheme'

const DoughnutGraph = forwardRef((props, ref) => {
  const { data, labels, className, graphColor, format, tooltipCallbackOptions = {} } = props

  const theme = useTheme()

  const themeGraphColors = useMemo(() => {
    // We use Set here to make sure we don't have duplicates
    return [
      ...new Set([theme[DEFAULT_GRAPH_COLOR], theme[GRAPH_COLOR_1], theme[GRAPH_COLOR_2], theme[GRAPH_COLOR_3]])
    ].filter(color => color !== undefined)
  }, [theme])

  const doughnutColors = useMemo(
    () => [
      ...insertIf(themeGraphColors.length > 0, ...themeGraphColors),
      purple,
      instagramOrange,
      darkBlue,
      facebookBlue,
      trafficGreenDark,
      trafficYellowDark
    ],
    [themeGraphColors]
  )

  const graphData = {
    labels,
    datasets: [
      {
        data: data,
        backgroundColor: doughnutColors,
        borderColor: 'white',
        borderWidth: 1,
        borderRadius: 4,
        borderAlign: 'inner',
        hoverBorderColor: 'white'
      }
    ]
  }

  const graphOptions = useMemo(
    () => ({
      ...getReusableGraphOptions({ format, graphColor, theme }),
      scales: {
        x: {
          display: false
        },
        y: {
          display: false
        }
      },
      plugins: {
        legend: {
          // limit legend max height to display maximum of 3 rows of labels
          maxHeight: 65,
          labels: {
            generateLabels: () =>
              labels.map((label, index) => ({
                text: label,
                fillStyle: doughnutColors[index],
                fontColor: doughnutColors[index],
                borderRadius: 2,
                datasetIndex: index,
                strokeStyle: 'white'
              })),
            boxWidth: 20,
            boxHeight: 8,
            font: {
              size: 14
            },
            padding: 6,
            borderRadius: 4
          }
        },
        tooltip: {
          ...getReusableGraphOptions({ format }).plugins.tooltip,
          footerMarginTop: 10,
          callbacks: {
            // hide title by returning null
            title: () => '',
            label: tooltipItem => tooltipItem.label,
            labelTextColor: tooltipItem => doughnutColors[tooltipItem.dataIndex],
            ...tooltipCallbackOptions
          }
        }
      }
    }),
    [format, doughnutColors, graphColor, theme, labels, tooltipCallbackOptions]
  )

  return (
    <div className={className}>
      <Doughnut type="doughnut" data={graphData} options={graphOptions} ref={ref} />
    </div>
  )
})

DoughnutGraph.propTypes = {
  data: PropTypes.array.isRequired,
  labels: PropTypes.array.isRequired,
  className: PropTypes.string,
  graphColor: PropTypes.string,
  format: PropTypes.string,
  tooltipCallbackOptions: PropTypes.object
}

export default DoughnutGraph
